// ============================================================
// WalletPopover — the bot wallet's only window into itself.
// ------------------------------------------------------------
// WHY THIS EXISTS, AND WHY IT ISN'T A PANEL:
//
// Phantom users have Phantom. Burner users hold their own key. The Privy
// embedded wallet has NO other home — no extension, no app, no second window.
// It exists only inside Cheddar. That's the gap this fills, and it's why this
// is Privy-only for now.
//
// It's a popover, not a Workspace panel, because every panel in this app is a
// thing you WATCH — chart, tape, scan, watchlist, messages, journal, all
// continuously informative, all earning a permanent cell. An address is not.
// Export you use once, ever. Receive: once, when you fund it. Panels are for
// vigils; this is a visit. A cell here would burn 70vh of phone screen showing
// an address you last looked at three weeks ago.
//
// And it isn't a section of WalletHoldings because WalletHoldings is not a
// wallet — App.jsx says so out loud: "WalletHoldings (the trade station)
// stays." It's 2,000 lines of size → buy/sell → positions, wearing wallet-*
// class names for historical reasons. Putting "export private key" one div from
// a one-click SELL-100% button is asking for a bad afternoon.
//
// STYLING: component-scoped <style>, same pattern ScanPanel already uses for
// its 3D switch. Everything is drawn from the theme tokens (--surface, --seam,
// --text-*, --accent, --connect-connected) so it restyles with the app for
// free and adds nothing to styles.css.
//
// PORTALED to document.body on purpose. BotSettingsMenu already learned this
// the hard way: zIndex 45 inside a z:10 stacking context paints under
// everything. A fixed, body-portaled layer can't be trapped by an ancestor.
// z-index 50 = the "menus" rung of the ladder.
// ============================================================
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useDualWallet } from './DualWalletContext'
import { sendSol, getSOLBalance } from '../utils/jupiter'

const GAP = 6          // px below the anchor button
const WIDTH = 244
const MARGIN = 8       // keep this far off the viewport edges

export default function WalletPopover({ anchorRef, onClose }) {
  const { privyWallet } = useDualWallet()
  const { address, publicKey, exportWallet, logout, signTransaction } = privyWallet

  const cardRef = useRef(null)
  const [pos, setPos] = useState(null)
  const [copied, setCopied] = useState(false)
  const [balance, setBalance] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  // ─── Send ───
  // Moves the BOT WALLET's SOL (privyWallet.address / .signTransaction), not the
  // mode-resolved active wallet — this popover is that wallet's window.
  //
  // Two-step: type recipient+amount, tap Send → the button ARMS ("Confirm →
  // <addr>"), a second tap executes. This is the confirmation gate. Privy's
  // global confirm modal is OFF (you turned it off for trading), but a send goes
  // to an ARBITRARY address, which is the one action where that matters — so the
  // APP asks, right here, regardless of Privy's setting. Same guard for burner
  // and browser wallets too. Auto-disarms so a primed Send can't sit waiting to
  // be hit by accident.
  const [sendOpen, setSendOpen] = useState(false)
  const [to, setTo] = useState('')
  const [amt, setAmt] = useState('')
  const [sendArmed, setSendArmed] = useState(false)
  const [sendResult, setSendResult] = useState(null)   // { txid } on success
  const armTimer = useRef(null)
  useEffect(() => () => clearTimeout(armTimer.current), [])
  useEffect(() => { setSendArmed(false) }, [to, amt])   // editing disarms

  // ─── Placement ───
  // Under the button, clamped to the viewport on BOTH axes. It used to clamp
  // only `left` and trust that `r.bottom + 6` left room below — which is true
  // today and stops being true the moment the card grows a row.
  //
  // The catch: clamping the bottom needs the card's HEIGHT, and you can't
  // measure a card you haven't rendered. The old code returned null until `pos`
  // existed, so offsetHeight was always 0 — the measurement could never happen.
  // So the card now always renders, just `visibility: hidden` until it's placed.
  // It's in the DOM (measurable) but not painted. A layout effect runs before
  // paint, so it's measured, positioned and revealed inside one frame — nothing
  // flashes at (0,0).
  //
  // No flip-above, deliberately: the anchor is a HEADER button, so there is
  // never room above it. The useful clamp is "slide up until you fit."
  const place = useCallback(() => {
    const el = anchorRef?.current
    const card = cardRef.current
    if (!el || !card) return
    const r = el.getBoundingClientRect()
    const h = card.offsetHeight
    const left = Math.max(MARGIN, Math.min(r.left, window.innerWidth - WIDTH - MARGIN))
    const maxTop = Math.max(MARGIN, window.innerHeight - MARGIN - h)
    setPos({ top: Math.min(r.bottom + GAP, maxTop), left })
  }, [anchorRef])

  useLayoutEffect(() => { place() }, [place])
  // Rotate the phone, or open the keyboard, and the viewport moves under it.
  useEffect(() => {
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [place])

  // Balance. A receive screen without one is useless — you fund the address and
  // then want to watch it land. Read-only, so it can't hurt anything.
  const refreshBalance = useCallback(() => {
    if (!publicKey) return
    setBalance(undefined)   // undefined = loading, null = failed, number = value
    getSOLBalance(publicKey).then(setBalance).catch(() => setBalance(null))
  }, [publicKey])
  useEffect(() => { refreshBalance() }, [refreshBalance])

  // Close on outside click / Escape. Pointerdown, not click: a click that STARTS
  // inside and drags out (selecting the address text) would otherwise dismiss.
  useEffect(() => {
    const onDown = (e) => {
      if (cardRef.current?.contains(e.target)) return
      if (anchorRef?.current?.contains(e.target)) return   // the button toggles itself
      onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onClose])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      setErr('Clipboard blocked — select and copy manually')
    }
  }

  const doSend = async () => {
    setErr(null)
    if (!sendArmed) {
      // First tap: validate cheaply, then arm. The real guards live in sendSol;
      // this is just so we don't arm on obvious garbage.
      if (!to.trim()) { setErr('Enter a recipient address'); return }
      if (!(Number(amt) > 0)) { setErr('Enter an amount'); return }
      setSendArmed(true)
      clearTimeout(armTimer.current)
      armTimer.current = setTimeout(() => setSendArmed(false), 4000)
      return
    }
    // Second tap: execute.
    clearTimeout(armTimer.current)
    setSendArmed(false)
    setBusy(true)
    try {
      const { txid } = await sendSol(to.trim(), Number(amt), address, signTransaction)
      setSendResult({ txid })
      setTo(''); setAmt('')
      refreshBalance()
    } catch (e) {
      setErr(e?.message || 'Send failed')
    } finally {
      setBusy(false)
    }
  }

  const doExport = async () => {
    setErr(null)
    setBusy(true)
    try {
      // Privy renders the modal. Its own warnings ARE the confirmation step —
      // no second guard needed here, and a homegrown one would just be noise in
      // front of a better one.
      await exportWallet()
    } catch (e) {
      // A user closing the modal rejects. That's not an error worth shouting.
      if (!/cancel|closed|reject/i.test(e?.message || '')) {
        setErr(e?.message || 'Export failed')
      }
    } finally {
      setBusy(false)
    }
  }

  if (!address) return null

  const balLabel =
    balance === undefined ? '···'
    : balance === null    ? '—'
    : `${balance.toFixed(4)} SOL`

  return createPortal(
    <>
      <style>{`
        .wpop {
          position: fixed; z-index: 50; width: ${WIDTH}px;
          background: var(--surface, #101018);
          border: 1px solid var(--seam, rgba(200,210,230,0.12));
          border-radius: 8px 0 8px 0; corner-shape: bevel;
          box-shadow: 0 12px 32px rgba(0,0,0,0.55);
          font-family: 'JetBrains Mono', monospace;
          overflow: hidden;
        }
        .wpop-rail {
          display: flex; align-items: center; gap: 8px;
          padding: 0 10px; height: 28px;
          background: var(--surface-rail, #14141d);
          border-bottom: 1px solid var(--seam, rgba(200,210,230,0.12));
        }
        .wpop-title {
          font-size: 9px; font-weight: 600; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--text-mid, #8a93a6);
          margin-right: auto;
        }
        /* The balance is the only LIVE thing on this card and it was styled like
           text — clickable to refresh with nothing saying so, and unreachable by
           keyboard because it was a span. Now it's a button with a glyph. */
        .wpop-bal {
          display: inline-flex; align-items: center; gap: 5px;
          background: none; border: 0; padding: 0;
          font-family: inherit; font-size: 10px;
          color: var(--connect-connected, #00ff88);
          cursor: pointer;
        }
        .wpop-bal:hover { opacity: 0.75; }
        .wpop-bal svg { opacity: 0.5; }
        .wpop-bal.loading svg { animation: wpop-spin 0.7s linear infinite; }
        @keyframes wpop-spin { to { transform: rotate(360deg); } }
        .wpop-x {
          background: none; border: 0; cursor: pointer; padding: 0 2px;
          color: var(--text-lo, #5a6172); font-size: 13px; line-height: 1;
        }
        .wpop-x:hover { color: var(--text-hi, #c8cfdc); }

        /* No QR. The address IS the receive UI — people paste, they don't scan,
           and a QR is a dependency plus a chunk of card for a gesture nobody
           here makes. So the address gets the space instead of a corner of it. */
        .wpop-addr {
          padding: 14px 14px 13px;
          font-size: 11px; line-height: 1.65; word-break: break-all;
          color: var(--text-hi, #c8cfdc); text-align: center; letter-spacing: 0.02em;
          user-select: all;   /* one click selects the whole thing */
          cursor: text;
        }

        .wpop-row {
          display: flex; align-items: center; width: 100%;
          background: none; border: 0; cursor: pointer;
          padding: 9px 12px; text-align: left;
          border-top: 1px solid var(--seam, rgba(200,210,230,0.12));
          font-family: inherit; font-size: 11px;
          color: var(--text-hi, #c8cfdc);
          transition: background 0.12s, color 0.12s;
        }
        .wpop-row:hover:not(:disabled) { background: var(--surface-hover, rgba(232,228,218,0.04)); }
        .wpop-row:disabled { opacity: 0.5; cursor: default; }
        .wpop-row .k { margin-left: auto; font-size: 9px; color: var(--text-lo, #5a6172); }
        .wpop-row.good { color: var(--connect-connected, #00ff88); }
        .wpop-row.arm { color: var(--accent, #f0a72b); }
        .wpop-row.arm:hover:not(:disabled) { background: rgba(240,167,43,0.08); }

        .wpop-send {
          border-top: 1px solid var(--seam, rgba(200,210,230,0.12));
          padding: 10px 12px 4px;
          display: flex; flex-direction: column; gap: 7px;
        }
        .wpop-send input {
          width: 100%; box-sizing: border-box;
          background: var(--surface-rail, #14141d);
          border: 1px solid var(--seam, rgba(200,210,230,0.12));
          border-radius: 4px; corner-shape: bevel;
          color: var(--text-hi, #c8cfdc);
          font-family: inherit; font-size: 10px;
          padding: 7px 8px; outline: none;
        }
        .wpop-send input:focus { border-color: var(--accent, #f0a72b); }
        .wpop-send input::placeholder { color: var(--text-lo, #5a6172); }
        /* no number spinners */
        .wpop-send input[type=number]::-webkit-inner-spin-button,
        .wpop-send input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .wpop-send input[type=number] { -moz-appearance: textfield; }
        .wpop-send-ok {
          font-size: 9px; color: var(--connect-connected, #00ff88);
          word-break: break-all; line-height: 1.4;
        }
        .wpop-send-ok a { color: inherit; text-decoration: underline; }

        /* LOG OUT IS NOT A MENU ITEM. It sat flush against "Export private key" —
           same size, same weight, one row apart, no separation. On a phone that's
           a mis-tap between "show me my private key" and "kill my session", and
           those two should not be neighbours.
           So: a gap, the rail's recessed ground, centered, smaller, muted. It
           reads as the card's footer, not as the third thing in a list. You have
           to mean it. The red only shows up on hover, once you're already there. */
        .wpop-row.danger {
          margin-top: 7px;
          justify-content: center;
          background: var(--surface-rail, #14141d);
          color: var(--text-mid, #8a93a6);
          font-size: 10px;
          padding: 8px 12px;
          letter-spacing: 0.04em;
        }
        .wpop-row.danger:hover:not(:disabled) {
          color: var(--down, #ff4d6a);
          background: rgba(255, 77, 106, 0.07);
        }

        .wpop-err {
          padding: 8px 12px; font-size: 10px; line-height: 1.4;
          color: var(--down, #ff4d6a);
          border-top: 1px solid var(--seam, rgba(200,210,230,0.12));
        }
      `}</style>

      <div
        className="wpop"
        ref={cardRef}
        style={{
          top: pos?.top ?? 0,
          left: pos?.left ?? 0,
          // Rendered (so it can be measured) but not painted (so you never see
          // it at 0,0). The layout effect above flips this within the frame.
          visibility: pos ? 'visible' : 'hidden',
        }}
      >
        <div className="wpop-rail">
          <span className="wpop-title">Bot Wallet</span>
          <button
            className={`wpop-bal${balance === undefined ? ' loading' : ''}`}
            onClick={refreshBalance}
            title="Refresh balance"
          >
            {balLabel}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
          </button>
          <button className="wpop-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* RECEIVE. Just the address, click-to-select. */}
        <div className="wpop-addr">{address}</div>

        <button className={`wpop-row${copied ? ' good' : ''}`} onClick={copy}>
          {copied ? 'Copied' : 'Copy address'}
        </button>

        {/* SEND. Collapsed to a row until you want it — the card is small and
            most opens are to receive/copy, not send. */}
        <button
          className="wpop-row"
          onClick={() => { setSendOpen(o => !o); setErr(null); setSendResult(null) }}
        >
          Send SOL
          <span className="k">{sendOpen ? '▲' : '▼'}</span>
        </button>
        {sendOpen && (
          <div className="wpop-send">
            <input
              type="text"
              inputMode="text"
              placeholder="Recipient address"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            <input
              type="number"
              inputMode="decimal"
              placeholder="Amount (SOL)"
              value={amt}
              onChange={(e) => setAmt(e.target.value)}
              min="0"
              step="any"
            />
            <button
              className={`wpop-row${sendArmed ? ' arm' : ''}`}
              style={{ borderTop: 0, justifyContent: 'center', padding: '8px 12px' }}
              onClick={doSend}
              disabled={busy}
            >
              {busy ? 'Sending…'
                : sendArmed ? `Confirm → ${to.slice(0, 4)}…${to.slice(-4)}`
                : 'Send'}
            </button>
            {sendResult && (
              <div className="wpop-send-ok">
                Sent ·{' '}
                <a href={`https://solscan.io/tx/${sendResult.txid}`} target="_blank" rel="noopener noreferrer">
                  {sendResult.txid.slice(0, 8)}…
                </a>
              </div>
            )}
          </div>
        )}

        {/* EXPORT. Import the key into Phantom and you keep the same address —
            that's why Solana exports a key and not a phrase. */}
        <button className="wpop-row" onClick={doExport} disabled={busy}>
          Export private key
          <span className="k">Phantom</span>
        </button>

        {/* LOG OUT lives HERE, behind a label, instead of being wired to a click
            on the address in the header. That was the whole problem: people click
            an address to read it or copy it, and that click was dropping the
            wallet. A labelled button inside a deliberate surface is a better
            guard than any two-tap arm — and see the CSS for why it's a footer
            rather than a row. */}
        <button className="wpop-row danger" onClick={() => { onClose(); logout() }}>
          Log out
        </button>

        {err && <div className="wpop-err">{err}</div>}
      </div>
    </>,
    document.body
  )
}